import { CloseOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Table } from "antd";
import { useState } from "react";
import { useTranslate } from "../i18n";
import { Person } from "../model/person";
import { CrudToolbar } from "./CrudToolbar";
import { EmptyState } from "./EmptyState";
import { PageHeader } from "./PageHeader";
import { personColumns } from "./personColumns";

/**
 * The picker shown when assigning people to a doctor or a caretaker.
 *
 * It is a list page with a different verb: the same header, action bar and
 * table, with Assign in place of Add and a way back out.
 */
export const AssignPersons = (props: {
    persons: Person[],
    title?: string,
    subtitle?: string,
    onPersonSearch?: (value: string) => void,
    onPersonsAssign?: (selectedPersons: Person[]) => void,
    onPersonReload?: () => void,
    onCancel?: () => void,
}) => {

    const t = useTranslate();
    const [personSearchTerm, setPersonSearchTerm] = useState<string>("");
    const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

    const onPersonSearch = () => {
        if (props.onPersonSearch != undefined && personSearchTerm != "") {
            props.onPersonSearch(personSearchTerm);
        }
    }

    const onSelectionPersonsChange = (selectedRowKeys: React.Key[], persons: Person[]) => {
        setSelectedKeys(selectedRowKeys);
        setSelectedPersons(persons);
    }

    const onPersonsAdd = () => {
        if (selectedPersons.length > 0 && props.onPersonsAssign != undefined) {
            props.onPersonsAssign(selectedPersons);
        }
    }

    const onCancel = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    }

    const columns = personColumns(t);

    return (<>
        <PageHeader
            eyebrow={t("assign.eyebrow")}
            title={props.title ?? t("assign.people.title")}
            count={props.persons.length}
            subtitle={props.subtitle}
            actions={
                <Button icon={<CloseOutlined />} onClick={onCancel}>
                    {t("common.cancel")}
                </Button>
            }
        />

        <CrudToolbar
            hasSelection={selectedPersons.length > 0}
            selectionLabel={
                selectedPersons.length === 1
                    ? [selectedPersons[0].firstName, selectedPersons[0].lastName].filter(Boolean).join(" ")
                    : t("assign.selection.count", { count: selectedPersons.length })
            }
            onClearSelection={() => onSelectionPersonsChange([], [])}
            selectionActions={
                <Button type="primary" icon={<PlusOutlined />} onClick={onPersonsAdd}>
                    {t("common.assign")}
                </Button>
            }
            onReload={props.onPersonReload}
            hint={t("crud.hint.assignPeople")}
            search={props.onPersonSearch != null ? {
                placeholder: t("list.search.people"),
                value: personSearchTerm,
                onChange: setPersonSearchTerm,
                onSearch: onPersonSearch
            } : undefined}
        />

        <div className="card card--flush">
            <Table
                rowKey={(person: Person) => person.id}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedKeys,
                    onChange: onSelectionPersonsChange
                }}
                columns={columns}
                dataSource={props.persons}
                locale={{
                    emptyText: <EmptyState
                        icon={<RightOutlined />}
                        title={t("empty.assign.title")}
                        hint={t("empty.assign.hint")}
                    />
                }}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                scroll={{ x: 'max-content' }}
            />
        </div>
    </>)

}
